import { Options } from 'highcharts/highstock';
import * as moment from 'moment';
import 'moment-timezone';

import { IColorTheme } from '../models/color-themes';
import { IDataset } from '../models/dataset';
import { DatasetType } from '../models/dataset-type';
import {
    disableYAxisLimitBands,
    hideUnusedDiscreteLabels,
    setDiscreteFormatters,
    setPlotBands,
    setRangeVisibility,
    setSeriesDisplayMode,
    setYAxisLabels,
    setYAxisRange,
    showAllDiscreteLabels
} from '../models/highstock-config';

/** Determines how a user can click and drag to zoom into a plot */
export enum ZoomMode {
    x = 'x',
    xy = 'xy'
}

/** Determines how to connect/display points in a series */
export enum SeriesDisplayMode {
    lines = 'lines',
    points = 'points',
    pointsAndLines = 'pointsAndLines'
}

export enum AxisFormat {
    auto = 'auto',
    secondsSinceT0 = 'secondsSinceT0',
    raw = 'raw'
}

export enum AxisScalingType {
    auto = 'auto',
    red = 'red',
    yellow = 'yellow',
    custom = 'custom'
}

/** Menu options that control how the plot/series is displayed */
export interface IMenuOptions {
    annotations: {
        show: boolean;
    };
    comments: {
        hideWhenEmpty: boolean;
    };
    legend: boolean;
    useGlobalSettings: boolean;
    overplot: IOverplotOptions;
    view: IViewOptions;
    yAxis: IYAxisOptions;
    xAxis: IXAxisOptions;
    dataDisplay: IDataDisplayOptions;
}

export interface IOverplotOptions {
    limitsDSID: string; // the dataset ID that we want to show limits for
    xAxisDSID: string; // the dataset ID that we want to show the xAxis for
}

export interface IViewOptions {
    navigator: boolean;
    scrollbar: boolean;
    limitAreas: boolean;
    yAxes: boolean;
    limitViolationColoring: boolean;
    events: boolean;
    eventTypes: number[];
}
export interface IAxisRange {
    type: AxisScalingType;
    low: number;
    high: number;
}

export interface IYAxisOptions {
    range: IAxisRange;
    scaling: 'linear' | 'logarithmic';
    hideUnusedDiscreteLabels: boolean;
    showNumericDiscreteValues: boolean;
    useMultipleAxes: boolean;
}

export interface IXAxisOptions {
    labels: AxisFormat;
    crosshair: boolean;
}

export interface IDataDisplayOptions {
    seriesDisplayMode: SeriesDisplayMode;
    allowGaps: boolean;
    thresholdRatio: number;
    showMinMaxRange: boolean;
}

export const DEFAULT_MENU_OPTIONS: IMenuOptions = {
    useGlobalSettings: false,
    annotations: {
        show: true
    },
    comments: {
        hideWhenEmpty: true
    },
    overplot: {
        limitsDSID: '',
        xAxisDSID: ''
    },
    legend: true,
    view: {
        navigator: true,
        scrollbar: true,
        limitAreas: true,
        yAxes: true,
        limitViolationColoring: false,
        events: true,
        eventTypes: []
    },
    yAxis: {
        range: {
            type: AxisScalingType.auto,
            low: null,
            high: null
        },
        scaling: 'linear',
        hideUnusedDiscreteLabels: true,
        showNumericDiscreteValues: false,
        useMultipleAxes: false
    },
    xAxis: {
        labels: AxisFormat.auto,
        crosshair: false
    },
    dataDisplay: {
        seriesDisplayMode: SeriesDisplayMode.lines,
        allowGaps: true,
        thresholdRatio: 5,
        showMinMaxRange: true
    }
};

/** update our plot config with current menu options  */
export function processMenuConfig(
    config: Options,
    menuOptions: IMenuOptions,
    colorTheme: IColorTheme,
    focusDataset: IDataset,
    plotHeight: number ): Options {
    // set range visibility
    config = setRangeVisibility( config, menuOptions.dataDisplay.showMinMaxRange );

    // set the x axis label formatters
    config = setXAxisLabels(
        menuOptions.xAxis.labels,
        config,
        'utc',
       'YYYY-MM-DD'
    );

    config.navigator.enabled = menuOptions.view.navigator;
    config.scrollbar.enabled = menuOptions.view.scrollbar;

    // set the plot to either lines, points, or points and lines
    config = setSeriesDisplayMode( config, menuOptions.dataDisplay.seriesDisplayMode );

    // set limit bands if enabled
    if ( menuOptions.view.limitAreas && focusDataset.metadata && focusDataset.metadata.limits.exist ) {
        config = setPlotBands( config, focusDataset.metadata.limits, colorTheme );
    } else {
        config = disableYAxisLimitBands( config );
    }

    // set y axis label formatters
    if ( !menuOptions.yAxis.showNumericDiscreteValues ) {
        if ( focusDataset.type === DatasetType.discrete ) {
            config = setDiscreteFormatters( config, focusDataset.metadata.typeConversions );
        }
    } else {
        config = setYAxisLabels( config );
    }

    if ( focusDataset.type === DatasetType.discrete ) {
        if ( menuOptions.yAxis.hideUnusedDiscreteLabels ) {
            const usedDiscrete = focusDataset.discreteValues;
            config = hideUnusedDiscreteLabels( config, focusDataset.yAxisBreaks, usedDiscrete );
        } else {
            config = showAllDiscreteLabels( config, focusDataset.metadata.typeConversions );
        }
    } else {
        // set y axis range
        const range = calculateYAxisRange( focusDataset, plotHeight, menuOptions.yAxis.range );
        if ( typeof range.low !== 'undefined' ) {
            config = setYAxisRange( config, range.low, range.high, 0.1 );
        }
    }

    if ( focusDataset.type === DatasetType.analog ) {
        config.yAxis[0].type = menuOptions.yAxis.scaling;
    }
    setYAxisCrosshairEnabled( config, colorTheme, menuOptions.xAxis.crosshair );
    config.legend.enabled = menuOptions.legend;

    return config;
}

/** enables or disables the horizontal crosshair (perpendicular to the y-axis) */
function setYAxisCrosshairEnabled( config: Options, colorTheme: IColorTheme, enabled: boolean ): void {
    if ( enabled ) {
        config.yAxis[0].crosshair = {
            snap: false,
            color: colorTheme.axis.crosshairColor
        };
    } else {
        config.yAxis[0].crosshair = false;
    }
}

/** apply the settings in menuOptions.yAxis.range */
function calculateYAxisRange( focusDataset: IDataset, plotHeight: number, range: IAxisRange ) {
    if ( focusDataset.type === DatasetType.discrete ) {
        // y-axis range shouldn't be changed for discrete plots
        return;
    }

    let low: number = null;
    let high: number = null;
    let limitExtremes;

    if ( range.type === 'custom' ) {
        limitExtremes = getExtremesWithPadding( range.low, range.high, plotHeight );
        low = limitExtremes.low;
        high = limitExtremes.high;
    }

    const limits = focusDataset.metadata.limits;
    if ( ( range.type === 'yellow' || range.type === 'red' ) && limits.exist ) {
        limitExtremes = range.type === 'yellow' ? getExtremesWithPadding( limits.yellow.low, limits.yellow.high, plotHeight )
            : range.type === 'red' ? getExtremesWithPadding( limits.red.low, limits.red.high, plotHeight )
            : { low: undefined, high: undefined };
        low = limitExtremes.low;
        high = limitExtremes.high;
    }

    return {
        low: low,
        high: high
    };
}

/** update the config with x axis label formatters */
function setXAxisLabels( labelFormat: string, config: Options, timezone: string, timeFormat: string ) {
    let formatter;
    let navigatorFormatter;

    const xAxisValueOffset = 0;

    if ( labelFormat === 'secondsSinceT0') {
        formatter = function() {
            return xAxisLabelFormatterT0( this );
        };
        navigatorFormatter = formatter;
    } else if ( labelFormat === 'raw' ) {
        formatter = function() {
            return this.value + xAxisValueOffset;
        };
        navigatorFormatter = formatter;
    } else if ( labelFormat === 'auto' ) {
        formatter = function() {
            return xAxisLabelFormatterDefault( this );
        };
        navigatorFormatter = function() {
            const tempDate = moment.utc( new Date( this.value + xAxisValueOffset ) );
            tempDate.tz( timezone );
            return tempDate.format( 'HH:mm' );
        };
    } else if ( labelFormat === null || labelFormat === undefined ) {
        // use the default highcharts formatter
        formatter = undefined;
        navigatorFormatter = undefined;
    } else {
        console.error( 'Programmer error. Unrecognized labelFormat:', labelFormat );
    }

    // apply the settings to the highchart object
    config.xAxis[0].labels.formatter = formatter;
    config.navigator.xAxis.labels.formatter = navigatorFormatter;

    function xAxisLabelFormatterDefault( labelObj ) {
        const tempDate = moment.utc( new Date( labelObj.value + xAxisValueOffset ) );
        tempDate.tz( timezone );
        return tempDate.format( timeFormat + '[<br>]HH:mm:ss');
    }
    function xAxisLabelFormatterT0( labelObj ) {
        return '+' + ( Math.round( (labelObj.value - labelObj.chart.xAxis[0].getExtremes().dataMin) / 1000) ) + 's';
    }

    return config;
}

/**
 * Adds padding to the plot so that there is always at least a bit of the yellow/red limit ranges visible.
 * Even if those ranges are very small
 */
function getExtremesWithPadding( low: number, high: number, plotHeightPixels: number, paddingPixels = 20) {
    // based on the chart pixel height and extremes we wish to show (given in y-axis units),
    // find the new extremes in y-axis units in order to have a certain amount of 'padding' in pixels on the top and bottom.
    const diff = (plotHeightPixels * high - paddingPixels * ( high + low )) / (plotHeightPixels - 2 * paddingPixels) - high;
    return { low: low - diff, high: high + diff };
}
